import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Card, Typography } from "@mui/material";
import ApprovalTrackerListPopUp from "./ApprovalTrackerListPopup";

const CategoryCount = ({ data, columns }) => {
    const [pieData, setPieData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isViewTracker, setIsViewTracker] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [duration, setDuration] = useState();

    const handleClose = () => {
        setIsViewTracker(false);
        setSelectedCategory(null);
    };

    const durationCategories = [
        "Within 1 Month",
        "1-3 Months",
        "3-6 Months",
        "More than 6 Months"
    ];

    const colors = {
        "Within 1 Month": "#00b4d8",
        "1-3 Months": "#0077b6",
        "3-6 Months": "#023e8a",
        "More than 6 Months": "#03045e"
    };

    const getDuration = (category) => {
        if (category === "Within 1 Month")
            return "0_30";
        else if (category === "1-3 Months")
            return "30_90";
        else if (category === "3-6 Months")
            return "90_180";
        else if (category === "More than 6 Months")
            return "180_plus";
        else
            return null;
    };

    const generateData = (data) => {
       
        const categories = [
            {
                name: "Within 1 Month",
                value: data?.within1Month || 0,
                description: "Items for less than 1 month",
                color: colors["Within 1 Month"]
            },
            {
                name: "1-3 Months",
                value: data?.oneToThreeMonths || 0,
                description: "Items for 1 to 3 months",
                color: colors["1-3 Months"]
            },
            {
                name: "3-6 Months",
                value: data?.threeToSixMonths || 0,
                description: "Items for 3 to 6 months",
                color: colors["3-6 Months"]
            },
            {
                name: "More than 6 Months",
                value: data?.moreThanSixMonths || 0,
                description: "Items for more than 6 months",
                color: colors["More than 6 Months"]
            }
        ];

        
        return categories
            .filter(category => category.value > 0)
            .map(category => ({
                name: category.name,
                y: category.value,
                count: category.value,
                description: category.description,
                color: category.color
            }));
    };

    useEffect(() => {
        if (data?.data) {
            const durationData = generateData(data.data);
            setPieData(durationData);
            const total = durationData.reduce((sum, item) => sum + item.y, 0);
            setTotalCount(total);
        }
    }, [data]);

    const handleCategoryClick = (categoryName) => {
        setSelectedCategory(categoryName);
        setDuration(getDuration(categoryName));
        setIsViewTracker(true);
    };

    const options = {
        chart: {
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: 'pie',
            height: 320,
            backgroundColor: '#FFFFFF',
            marginTop: 0,
            margingLeft:0,
            paddingTop:0,
            paddingLeft:0,
            spacingTop: 0
        },
        title: {
            text: `Total: ${totalCount}`,
            align: 'center',
            verticalAlign: 'middle',
            y: 10,
            x: -90,
            style: {
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#333'
            }
        },
        tooltip: {
            formatter: function () {
                return `<b>${this.point.name}</b><br/>
                Count: <b>${this.point.y}</b><br/>
                Percentage: <b>${this.point.percentage.toFixed(1)}%</b><br/>
                ${this.point.description || ''}`;
            },
            useHTML: true
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b><br/>{point.y} ({point.percentage:.1f}%)',
                    distance: 20,
                    style: {
                        fontWeight: 'normal',
                        fontSize: '12px'
                    }
                },
                showInLegend: true,
                innerSize: '50%',
                point: {
                    events: {
                        click: function (event) {
                            handleCategoryClick(this.name);
                        }
                    }
                }
            }
        },
        series: [{
            name: 'Duration Categories',
            colorByPoint: true,
            data: pieData,
            size: '85%',
            innerSize: '60%',
            dataLabels: {
                enabled: true
            }
        }],
        legend: {
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemMarginTop: 10,
            itemMarginBottom: 10,
            labelFormat: '{name}: <b>{y}</b>'
        },
        colors: pieData.map(item => item.color),
        credits: {
            enabled: false
        }
    };

    const getCategoryStats = () => {
        return pieData.map(item => ({
            category: item.name,
            count: item.y,
            percentage: totalCount > 0 ? ((item.y / totalCount) * 100).toFixed(1) : 0,
            color: item.color
        }));
    };

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "3px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
                    width: '100%',
                    height: 400,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#ffffff',
                }}
            >
                <Box sx={{
                    width: '100%',
                    borderBottom: "1px solid #E4E4E4",
                    px: 1.5,
                    py: 1.25,
                    mb: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    minHeight: 70,
                }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 600,
                            fontSize: '15px',
                            lineHeight: '21px',
                            letterSpacing: "0px",
                            mb: 0.5
                        }}>
                            Validity Duration Distribution
                        </Typography>
                        <Typography variant="subtitle1" sx={{
                            fontWeight: 500,
                            fontSize: '11px',
                            lineHeight: "16px",
                            color: "#7D7F81",
                            letterSpacing: "1%",
                            fontFamily: "Poppins, sans-serif",
                        }}>
                            Showing items by validity duration
                        </Typography>
                    </Box>

                    <Box sx={{
                        backgroundColor: '#f0f7ff',
                        padding: '8px 16px',
                        borderRadius: '3px',
                        border: '1px solid #d0e3ff'
                    }}>
                        <Typography sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#0066cc',
                            fontFamily: "Outfit, sans-serif",
                        }}>
                            Total: {totalCount} items
                        </Typography>
                    </Box>
                </Box>

                {pieData.length > 0 ? (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                    />
                ) : (
                    <Box
                        sx={{
                            height: 320,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            pb: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "16px",
                                fontWeight: 500,
                                color: "#9E9E9E",
                            }}
                        >
                            No data available
                        </Typography>
                    </Box>
                )}
            </Card>
            {isViewTracker && (
                <ApprovalTrackerListPopUp
                    open={isViewTracker}
                    data={{
                        durationTag: duration,
                    }}
                    handleClose={handleClose}
                    columns={columns}
                />
            )}
        </>
    );
};

export default CategoryCount;
